import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/App";
import { Loader2 } from "lucide-react";

interface Organization {
  id: string;
  name: string;
}

interface CreateBusinessUnitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  organizationId?: string; // Pre-fill when creating from PM detail page
}

export function CreateBusinessUnitDialog({
  open,
  onOpenChange,
  onSuccess,
  organizationId: propOrganizationId,
}: CreateBusinessUnitDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organizationId: propOrganizationId || user?.organizationId || "",
    cardProgramName: "",
    maxCardsPerCustomer: 5,
    status: "ACTIVE",
  });

  const { data: organizations } = useQuery<Organization[]>({
    queryKey: ["organizations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/organizations");
      if (!response.ok) throw new Error("Failed to fetch program managers");
      return response.json();
    },
    enabled: open && user?.role === "SUPER",
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/business-units", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create business unit");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-units"] });
      queryClient.invalidateQueries({ queryKey: ["pm-business-units"] });
      toast({
        title: "Success",
        description: "Business Unit created successfully",
      });
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      organizationId: propOrganizationId || user?.organizationId || "",
      cardProgramName: "",
      maxCardsPerCustomer: 5,
      status: "ACTIVE",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Business Unit</DialogTitle>
          <DialogDescription>Add a new business unit</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {user?.role === "SUPER" && (
              <div className="space-y-2">
                <Label htmlFor="organizationId">Organization *</Label>
                <Select
                  value={formData.organizationId}
                  onValueChange={(v) =>
                    setFormData({ ...formData, organizationId: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations?.map((pm) => (
                      <SelectItem key={pm.id} value={pm.id}>
                        {pm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Organization Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Tech Corp Australia"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="admin@corp.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+61 400 000 000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cardProgramName">Card Program Name</Label>
              <Input
                id="cardProgramName"
                value={formData.cardProgramName}
                onChange={(e) =>
                  setFormData({ ...formData, cardProgramName: e.target.value })
                }
                placeholder="Employee Expense Cards"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxCardsPerCustomer">Max Cards Per Customer</Label>
              <Input
                id="maxCardsPerCustomer"
                type="number"
                value={formData.maxCardsPerCustomer}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maxCardsPerCustomer: parseInt(e.target.value),
                  })
                }
                min={1}
                max={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
